import { useQuery } from '@tanstack/react-query';
import {
  useNodesInitialized,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled';
import { type FC, useCallback, useEffect } from 'react';

export interface AlgorithmJoinsWithMappingsGraphLayouterProps {
  onReady?: () => void;
}

export const AlgorithmJoinsWithMappingsGraphLayouter: FC<
  AlgorithmJoinsWithMappingsGraphLayouterProps
> = (props) => {
  const { onReady } = props;
  const { getNodes, getEdges, setNodes } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const updateNodeInternals = useUpdateNodeInternals();

  const { data } = useQuery({
    queryKey: ['laidOutNodesAndEdges'],
    staleTime: 'static',
    enabled: nodesInitialized,
    queryFn: useCallback(async () => {
      const nodes = getNodes();

      const layout = await elk.layout({
        id: 'root',
        children: nodes.map(
          // move dimension props from `measured` object to node.width/node.height as elk expects
          ({ measured: { width, height } = {}, ...node }) => ({
            ...node,
            width,
            height,
          }),
        ),
        edges: getEdges() as any,
      });

      return (layout.children || []).map(
        // move width/height props from node.width/node.height to `measured` object as react-flow expects
        ({ x = 0, y = 0, ...node }) => ({
          ...node,
          position: { x, y },
        }),
      );
    }, [getNodes, getEdges]),
  });

  useEffect(() => {
    if (data) {
      setNodes(data);
      updateNodeInternals(data.map((n) => n.id));
      onReady?.();
    }
  }, [setNodes, data, updateNodeInternals, onReady]);

  return null;
};

const elk = new ELK({
  defaultLayoutOptions: {
    'elk.algorithm': 'layered',
    'elk.layered.spacing.nodeNodeBetweenLayers': '100',
    'elk.spacing.nodeNode': '80',
    'elk.direction': 'RIGHT',
  },
});
